import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';

/**
 * Panel variants using class-variance-authority
 */
const panelVariants = cva('rounded-lg border bg-card', {
  variants: {
    variant: {
      default: 'border-border',
      bordered: 'border-2 border-border',
      ghost: 'border-transparent',
    },
    padding: {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    },
  },
  defaultVariants: {
    variant: 'default',
    padding: 'md',
  },
});

export interface PanelProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof panelVariants> {}

/**
 * Panel Component
 *
 * A bordered container component for grouping related content.
 * Similar to Card but with simpler styling and no composition components.
 *
 * @example
 * <Panel>
 *   <p>Panel content goes here</p>
 * </Panel>
 *
 * @example
 * // Panel with custom padding
 * <Panel padding="lg">
 *   <h3>Title</h3>
 *   <p>Content with more padding</p>
 * </Panel>
 *
 * @example
 * // Ghost panel (no border)
 * <Panel variant="ghost">
 *   <p>Content without visible border</p>
 * </Panel>
 */
const Panel = React.forwardRef<HTMLDivElement, PanelProps>(
  ({ className, variant, padding, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(panelVariants({ variant, padding }), className)}
      {...props}
    />
  ),
);
Panel.displayName = 'Panel';

export { Panel, panelVariants };
